import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, FormControlLabel, Radio, RadioGroup } from 'edenred-ui';
import { useTheme } from 'styled-components';
import { useFormContext } from 'react-hook-form';

import {
  FundsLoadingTypes,
  LimitType,
  LUNCH
} from '@epi-constants/benefitTypes';
import { DailyLimitField } from '@epi-containers/ManageBenefitGroups/components/DailyLimitField';
import { ICreateBenefitGroupForm } from '@epi-models/pages/BenefitGroups';
import { LunchFundsLoadingValues } from '@epi-models/pages/Home';

import { BenefitSettingsTooltip } from '../../BenefitSettingsTooltip/BenefitSettingsTooltip';

import { GeneralSettingsLoadTypeNotification } from './GeneralSettingsLoadTypeNotification';

interface GeneralSettingsProps {
  maxValue?: number;
  disabled: boolean;
}

const GeneralSettings = ({ maxValue, disabled }: GeneralSettingsProps) => {
  const { fontSemiBold, sizeSmall, gray50, gray85 } = useTheme();
  const { t } = useTranslation();
  const { watch, setValue, trigger } =
    useFormContext<ICreateBenefitGroupForm>();

  const spendingLimitFieldValue = watch(
    'benefitSettings.lunch.isMaximumSpendingLimit'
  );

  const handleOnChangeSpendingLimit = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setValue(
      'benefitSettings.lunch.isMaximumSpendingLimit',
      event.target.value === LimitType.maximum
    );
    if (event.target.value === LimitType.maximum) {
      setValue('benefitSettings.lunch.maxAmountPerPeriod', maxValue);
      trigger('benefitSettings.lunch.maxAmountPerPeriod');
    }
  };

  const fundsLoadingTypeFieldValue = watch(
    'benefitSettings.lunch.fundsLoadingType'
  );

  const handleOnChangeFundsLoadingType = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setValue(
      'benefitSettings.lunch.fundsLoadingType',
      event.target.value as LunchFundsLoadingValues
    );
  };

  return (
    <Box display="flex" flexDirection="row" gap={3}>
      <Box width="50%">
        <Box display="flex" alignItems="center" mb={1}>
          <Box
            color={disabled ? gray50 : gray85}
            fontSize={sizeSmall}
            fontWeight={fontSemiBold}
            id="lounari-loading-type-title"
          >
            {t('components.manage_benefits.spending_limit_field.title')}
          </Box>

          <BenefitSettingsTooltip
            descriptionKey={
              'components.manage_benefits.spending_limit_field.info'
            }
          />
        </Box>
        <RadioGroup
          onChange={handleOnChangeSpendingLimit}
          value={spendingLimitFieldValue ? LimitType.maximum : LimitType.custom}
        >
          <FormControlLabel
            value={LimitType.maximum}
            control={<Radio />}
            label={t(
              'components.manage_benefits.spending_limit_field.labels.maximum'
            )}
            disabled={disabled}
          />
          <FormControlLabel
            value={LimitType.custom}
            control={<Radio />}
            label={t(
              'components.manage_benefits.spending_limit_field.labels.custom'
            )}
            disabled={disabled}
          />
        </RadioGroup>
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          color={disabled ? gray50 : gray85}
        >
          <Box width="220px">
            <DailyLimitField
              label={t(`components.manage_benefit_lounari.detail_limit_label`)}
              readOnly={spendingLimitFieldValue}
              benefitType={LUNCH.toLowerCase()}
              options={[]}
              name={`benefitSettings.lunch.maxAmountPerPeriod`}
              fundsLoadingTypeName={`benefitSettings.lunch.fundsLoadingType`}
              maxAmounts={[]}
              disabled={disabled}
              customColor={disabled ? gray50 : gray85}
              fullWidth={false}
            />
          </Box>
          <BenefitSettingsTooltip
            descriptionKey={`components.manage_benefit_lunch.detail_daily_limit_statement`}
          />
        </Box>
      </Box>
      <Box width="50%">
        <Box display="flex" alignItems="center" mb={1}>
          <Box
            fontSize={sizeSmall}
            fontWeight={fontSemiBold}
            color={disabled ? gray50 : gray85}
            id="lounari-loading-type-title"
          >
            {t('components.manage_benefits.loading_type_field.title')}
          </Box>

          <BenefitSettingsTooltip
            descriptionKey={
              'components.manage_benefits.loading_type_field.lunch_info'
            }
          />
        </Box>
        <RadioGroup
          value={fundsLoadingTypeFieldValue}
          onChange={handleOnChangeFundsLoadingType}
        >
          <FormControlLabel
            value={FundsLoadingTypes.TopUpRequest}
            control={<Radio />}
            label={t(
              'components.manage_benefits.loading_type_field.labels.top_up_request'
            )}
            disabled={disabled}
          />
          <FormControlLabel
            value={FundsLoadingTypes.DirectLoad}
            control={<Radio />}
            label={t(
              'components.manage_benefits.loading_type_field.labels.direct_load'
            )}
            disabled={disabled}
          />
        </RadioGroup>
        <GeneralSettingsLoadTypeNotification isHidden={disabled} />
      </Box>
    </Box>
  );
};

export { GeneralSettings };
